import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Divider,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  alpha,
  useTheme
} from '@mui/material';
import {
  PictureAsPdf as PdfIcon,
  TableChart as CsvIcon,
  Upload as UploadIcon,
  Download as DownloadIcon,
  History as VersionIcon,
  Compare as CompareIcon,
  FileCopy as TemplateIcon,
  Add as AddIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import {
  exportTopSheetPDF,
  exportDetailPDF,
  exportCostReportPDF,
  exportCSV,
  importCSV,
  getVersions,
  createVersion,
  compareVersions,
  getTemplates,
  applyTemplate,
  formatCurrency,
  downloadBlob
} from '../services/budgetService';

function BudgetReports({ projectId, budget, onBudgetUpdate, onOpenPayrollImport }) {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Versions
  const [versions, setVersions] = useState([]);
  const [versionDialogOpen, setVersionDialogOpen] = useState(false);
  const [newVersionName, setNewVersionName] = useState('');
  const [compareDialogOpen, setCompareDialogOpen] = useState(false);
  const [compareV1, setCompareV1] = useState('');
  const [compareV2, setCompareV2] = useState('');
  const [comparison, setComparison] = useState(null);
  
  // Templates
  const [templates, setTemplates] = useState([]);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  
  // CSV Import
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [csvFile, setCsvFile] = useState(null);

  useEffect(() => {
    loadVersions();
    loadTemplates();
  }, [projectId]);

  const loadVersions = async () => {
    try {
      const data = await getVersions(projectId);
      setVersions(data);
    } catch (err) {
      console.error('Failed to load versions:', err);
    }
  };

  const loadTemplates = async () => {
    try {
      const data = await getTemplates();
      setTemplates(data);
    } catch (err) {
      console.error('Failed to load templates:', err);
    }
  };

  // PDF Exports
  const handleExportTopSheet = async () => {
    try {
      setLoading(true);
      const blob = await exportTopSheetPDF(projectId);
      downloadBlob(blob, `${budget?.name || 'budget'}-topsheet.pdf`);
      setSuccess('Top Sheet PDF exported successfully');
    } catch (err) {
      setError('Failed to export Top Sheet PDF');
    } finally {
      setLoading(false);
    }
  };

  const handleExportDetail = async () => {
    try {
      setLoading(true);
      const blob = await exportDetailPDF(projectId);
      downloadBlob(blob, `${budget?.name || 'budget'}-detail.pdf`);
      setSuccess('Detail Budget PDF exported successfully');
    } catch (err) {
      setError('Failed to export Detail PDF');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCostReport = async () => {
    try {
      setLoading(true);
      const blob = await exportCostReportPDF(projectId);
      downloadBlob(blob, `${budget?.name || 'budget'}-cost-report.pdf`);
      setSuccess('Cost Report PDF exported successfully');
    } catch (err) {
      setError('Failed to export Cost Report PDF');
    } finally {
      setLoading(false);
    }
  };

  // CSV Export/Import
  const handleExportCSV = async () => {
    try {
      setLoading(true);
      const blob = await exportCSV(projectId);
      downloadBlob(blob, `${budget?.name || 'budget'}.csv`);
      setSuccess('CSV exported successfully');
    } catch (err) {
      setError('Failed to export CSV');
    } finally {
      setLoading(false);
    }
  };

  const handleImportCSV = async () => {
    if (!csvFile) return;
    
    try {
      setLoading(true);
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const csvData = e.target.result;
          await importCSV(projectId, budget._id, csvData);
          setSuccess('CSV imported successfully');
          setImportDialogOpen(false);
          setCsvFile(null);
          onBudgetUpdate?.();
        } catch (err) {
          setError('Failed to import CSV');
        } finally {
          setLoading(false);
        }
      };
      reader.readAsText(csvFile);
    } catch (err) {
      setError('Failed to read CSV file');
      setLoading(false);
    }
  };

  // Versions
  const handleCreateVersion = async () => {
    try {
      setLoading(true);
      await createVersion(projectId, budget._id, newVersionName);
      setSuccess('New version created successfully');
      setVersionDialogOpen(false);
      setNewVersionName('');
      loadVersions();
      onBudgetUpdate?.();
    } catch (err) {
      setError('Failed to create version');
    } finally {
      setLoading(false);
    }
  };

  const handleCompareVersions = async () => {
    if (!compareV1 || !compareV2) return;
    
    try {
      setLoading(true);
      const data = await compareVersions(projectId, compareV1, compareV2);
      setComparison(data);
    } catch (err) {
      setError('Failed to compare versions');
    } finally {
      setLoading(false);
    }
  };

  // Templates
  const handleApplyTemplate = async (templateId) => {
    if (!window.confirm('This will replace all current line items. Continue?')) return;
    
    try {
      setLoading(true);
      await applyTemplate(projectId, templateId);
      setSuccess('Template applied successfully');
      setTemplateDialogOpen(false);
      onBudgetUpdate?.();
    } catch (err) {
      setError('Failed to apply template');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Export Section */}
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
        Export Reports
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <PdfIcon color="error" />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Top Sheet
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Executive summary with category totals
              </Typography>
            </CardContent>
            <CardActions>
              <Button 
                size="small" 
                startIcon={<DownloadIcon />}
                onClick={handleExportTopSheet}
                disabled={loading || !budget}
              >
                Export PDF
              </Button>
            </CardActions>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <PdfIcon color="error" />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Detail Budget
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Full line item breakdown by account
              </Typography>
            </CardContent>
            <CardActions>
              <Button 
                size="small" 
                startIcon={<DownloadIcon />}
                onClick={handleExportDetail}
                disabled={loading || !budget}
              >
                Export PDF
              </Button>
            </CardActions>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <PdfIcon color="error" />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Cost Report
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Budget vs actual with recent expenses
              </Typography>
            </CardContent>
            <CardActions>
              <Button 
                size="small" 
                startIcon={<DownloadIcon />}
                onClick={handleExportCostReport}
                disabled={loading || !budget}
              >
                Export PDF
              </Button>
            </CardActions>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CsvIcon color="success" />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  CSV Export
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Spreadsheet format for Excel/Sheets
              </Typography>
            </CardContent>
            <CardActions>
              <Button 
                size="small" 
                startIcon={<DownloadIcon />}
                onClick={handleExportCSV}
                disabled={loading || !budget}
              >
                Export CSV
              </Button>
            </CardActions>
          </Card>
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      {/* Import & Templates Section */}
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
        Import & Templates
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <UploadIcon color="primary" />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Import CSV
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Import line items from CSV file
              </Typography>
            </CardContent>
            <CardActions>
              <Button 
                size="small" 
                startIcon={<UploadIcon />}
                onClick={() => setImportDialogOpen(true)}
                disabled={loading || !budget}
              >
                Import
              </Button>
            </CardActions>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <TemplateIcon color="secondary" />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Budget Templates
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Start from a pre-built template
              </Typography>
            </CardContent>
            <CardActions>
              <Button 
                size="small" 
                startIcon={<TemplateIcon />}
                onClick={() => setTemplateDialogOpen(true)}
                disabled={loading}
              >
                Browse Templates
              </Button>
            </CardActions>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <UploadIcon color="success" />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Import Payroll Actuals
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Upload EP / Greenslate / Wrapbook exports to update budget actuals.
              </Typography>
            </CardContent>
            <CardActions>
              <Button
                size="small"
                startIcon={<UploadIcon />}
                onClick={onOpenPayrollImport}
                disabled={loading || !budget}
              >
                Import Payroll
              </Button>
            </CardActions>
          </Card>
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      {/* Version Control Section */}
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
        Version Control
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Budget Versions
              </Typography>
              <Button 
                size="small" 
                startIcon={<AddIcon />}
                onClick={() => setVersionDialogOpen(true)}
                disabled={!budget}
              >
                New Version
              </Button>
            </Box>
            <List dense>
              {versions.length === 0 ? (
                <ListItem>
                  <ListItemText 
                    primary="No versions yet" 
                    secondary="Create a version to track changes"
                  />
                </ListItem>
              ) : (
                versions.map((v) => (
                  <ListItem key={v._id}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            v{v.version}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {v.name}
                          </Typography>
                          {v.version === budget?.version && (
                            <Chip label="Current" size="small" color="primary" />
                          )}
                        </Box>
                      }
                      secondary={`${formatCurrency(v.totals?.grandTotal)} • ${new Date(v.createdAt).toLocaleDateString()}`}
                    />
                  </ListItem>
                ))
              )}
            </List>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Compare Versions
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  select
                  label="Version 1"
                  value={compareV1}
                  onChange={(e) => setCompareV1(e.target.value)}
                  fullWidth
                  size="small"
                >
                  {versions.map((v) => (
                    <MenuItem key={v._id} value={v.version}>
                      v{v.version} - {v.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  select
                  label="Version 2"
                  value={compareV2}
                  onChange={(e) => setCompareV2(e.target.value)}
                  fullWidth
                  size="small"
                >
                  {versions.map((v) => (
                    <MenuItem key={v._id} value={v.version}>
                      v{v.version} - {v.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  startIcon={<CompareIcon />}
                  onClick={handleCompareVersions}
                  disabled={!compareV1 || !compareV2 || loading}
                  fullWidth
                >
                  Compare
                </Button>
              </Grid>
            </Grid>

            {comparison && (
              <Box sx={{ mt: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell></TableCell>
                      <TableCell align="right">v{comparison.version1.version}</TableCell>
                      <TableCell align="right">v{comparison.version2.version}</TableCell>
                      <TableCell align="right">Diff</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>Grand Total</TableCell>
                      <TableCell align="right">{formatCurrency(comparison.version1.grandTotal)}</TableCell>
                      <TableCell align="right">{formatCurrency(comparison.version2.grandTotal)}</TableCell>
                      <TableCell 
                        align="right"
                        sx={{ color: comparison.differences.grandTotal > 0 ? 'error.main' : 'success.main' }}
                      >
                        {comparison.differences.grandTotal > 0 ? '+' : ''}{formatCurrency(comparison.differences.grandTotal)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>ATL</TableCell>
                      <TableCell align="right">{formatCurrency(comparison.version1.atl)}</TableCell>
                      <TableCell align="right">{formatCurrency(comparison.version2.atl)}</TableCell>
                      <TableCell 
                        align="right"
                        sx={{ color: comparison.differences.atl > 0 ? 'error.main' : 'success.main' }}
                      >
                        {comparison.differences.atl > 0 ? '+' : ''}{formatCurrency(comparison.differences.atl)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>BTL Production</TableCell>
                      <TableCell align="right">{formatCurrency(comparison.version1.btlProduction)}</TableCell>
                      <TableCell align="right">{formatCurrency(comparison.version2.btlProduction)}</TableCell>
                      <TableCell 
                        align="right"
                        sx={{ color: comparison.differences.btlProduction > 0 ? 'error.main' : 'success.main' }}
                      >
                        {comparison.differences.btlProduction > 0 ? '+' : ''}{formatCurrency(comparison.differences.btlProduction)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Create Version Dialog */}
      <Dialog open={versionDialogOpen} onClose={() => setVersionDialogOpen(false)}>
        <DialogTitle>Create New Version</DialogTitle>
        <DialogContent>
          <TextField
            label="Version Name"
            value={newVersionName}
            onChange={(e) => setNewVersionName(e.target.value)}
            fullWidth
            sx={{ mt: 1 }}
            placeholder={`${budget?.name} v${(budget?.version || 0) + 1}`}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVersionDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateVersion} disabled={loading}>
            Create Version
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import CSV Dialog */}
      <Dialog open={importDialogOpen} onClose={() => setImportDialogOpen(false)}>
        <DialogTitle>Import CSV</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Upload a CSV file with columns: Account Code, Description, Category, Units, Unit Type, Rate, Multiplier
          </Typography>
          <input
            type="file"
            accept=".csv"
            onChange={(e) => setCsvFile(e.target.files[0])}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialogOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleImportCSV} 
            disabled={loading || !csvFile}
          >
            Import
          </Button>
        </DialogActions>
      </Dialog>

      {/* Templates Dialog */}
      <Dialog 
        open={templateDialogOpen} 
        onClose={() => setTemplateDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Budget Templates</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {templates.map((template) => (
              <Grid item xs={12} sm={6} md={4} key={template.id}>
                <Card 
                  sx={{ 
                    height: '100%',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: 'primary.main',
                      transform: 'translateY(-2px)',
                      boxShadow: 2
                    }
                  }}
                  onClick={() => handleApplyTemplate(template.id)}
                >
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {template.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {template.description}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip 
                        label={formatCurrency(template.totalBudget)} 
                        size="small" 
                        color="primary"
                        variant="outlined"
                      />
                      <Chip 
                        label={`${template.shootDays} days`} 
                        size="small" 
                        variant="outlined"
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplateDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default BudgetReports;
